import { useState } from 'react';
import { format, startOfDay, endOfDay } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';
import poliApiGatewayRequest from 'src/utils/poliApiGatewayRequest';

import store from 'src/store';
import { receiveMessage } from 'src/store/modules/chat/actions';
import { toast } from 'react-toastify';
import glotaTranslationService from 'src/services/glota-translation.service';
import { useSelector } from 'react-redux';
import { useGlotaLocale } from 'src/hooks/useGlotaTranslation';

interface UseSummarizeProps {
  chat: any; // Tipo do chat - você pode definir uma interface mais específica
}

interface DateRange {
  startDate: Date;
  endDate: Date;
}

type Language = 'pt-br' | 'en-us' | 'es';

export const useSummarize = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const user = useSelector((state: any) => state?.general?.user);

  const { backendLocale } = useGlotaLocale();

  // Gera um resumo de uma mensagem sem enviar
  const handleGenerateSummary = async (chat: any, range: DateRange) => {
    try {
      setIsGeneratingSummary(true);
      const normalizeDateToRequest = {
        startDate: format(
          startOfDay(new Date(range.startDate)),
          'yyyy-MM-dd HH:mm:ss'
        ),
        endDate: format(
          endOfDay(new Date(range.endDate)),
          'yyyy-MM-dd HH:mm:ss'
        ),
      };

      const summary = await poliApiGatewayRequest
        .post('/ia/messages/summarize', {
          language: backendLocale,
          contactUuid: String(chat.id),
          userUuid: user?.id,
          publish: false,
          ...normalizeDateToRequest,
        })
        .then((res) => {
          return res;
        })
        .catch((err) => {
          return err.response;
        });

      if (summary?.status !== 200) {
        if (summary?.status === 404) {
          toast.error(
            glotaTranslationService.translateSync(
              'spa.chat.systemMessages.insufficientMessagesForSummary'
            )
          );
          return {
            status: 404,
            data: null,
          };
        }
        toast.error('Erro ao gerar o resumo de conversa.');
        return {
          status: 500,
          data: null,
        };
      }

      setIsGeneratingSummary(false);
      return {
        data: summary.data,
        status: summary.status,
      };
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  // Envia um resumo gerado e editado pelo usuário
  const handlePublishSummary = async (
    chat: any,
    summary: any,
    range: DateRange
  ) => {
    try {
      setIsLoading(true);
      const normalizeDateToRequest = {
        startDate: format(
          startOfDay(new Date(range.startDate)),
          'yyyy-MM-dd HH:mm:ss'
        ),
        endDate: format(
          endOfDay(new Date(range.endDate)),
          'yyyy-MM-dd HH:mm:ss'
        ),
      };

      await poliApiGatewayRequest
        .post('/ia/messages/publish_summary', {
          ...summary,
          ...normalizeDateToRequest,
          userUuid: user?.id,
        })
        .then((res) => {
          const summaryData = res.data;
          const message = {
            ...chat.last_message,
            users: chat.last_message.users,
            cuid: uuidv4(),
            updated_at: new Date().toISOString(),
            body: `${summaryData.summary}`,
            type: 'summary',
            event: 'summarize',
            messageType: 'summary',
            uid: uuidv4(),
            id: uuidv4(),
            direction: 'sent',
            dir: null,
            chatType: 'user',
            messageId: uuidv4(),
            time: Math.floor(Date.now() / 1000).toString(),
            dtm: Math.floor(Date.now() / 1000).toString(),
            ack: null,
          };

          store.dispatch(receiveMessage(message));
          return res.data;
        })
        .catch((err) => {
          toast.error(
            <div style={{ margin: 0 }}>
              <span style={{ fontWeight: 'bold' }}>
                Erro ao salvar o resumo!
              </span>
              <br />
              <span>O contato ainda pode ser transferido sem resumo.</span>
            </div>
          );
          return err.response.data;
        });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSummarize = async (
    chat: any,
    range: DateRange,
    needAttachSummaryInChat = true,
    context: Array<string> = []
  ) => {
    setIsLoading(true);

    const normalizeDateToRequest = {
      startDate: format(
        startOfDay(new Date(range.startDate)),
        'yyyy-MM-dd HH:mm:ss'
      ),
      endDate: format(endOfDay(new Date(range.endDate)), 'yyyy-MM-dd HH:mm:ss'),
      context,
      language: backendLocale,
    };

    let message = {
      ...chat.last_message,
      users: chat.last_message.users,
      cuid: uuidv4(),
      updated_at: new Date().toISOString(),
      body: `Gerando resumo...`,
      type: 'summary_loading',
      event: 'summarize',
      messageType: 'summary',
      uid: uuidv4(),
      id: uuidv4(),
      direction: 'sent',
      dir: null,
      chatType: 'user',
      messageId: uuidv4(),
      time: Math.floor(Date.now() / 1000).toString(),
      dtm: Math.floor(Date.now() / 1000).toString(),
      ack: null,
    };

    try {
      if (needAttachSummaryInChat) {
        store.dispatch(receiveMessage(message));
      }

      const summary = await poliApiGatewayRequest.post(
        '/ia/messages/summarize',
        {
          contactUuid: String(chat.id),
          userUuid: user?.id,
          ...normalizeDateToRequest,
        }
      );

      if (needAttachSummaryInChat) {
        const summaryData = summary.data;

        message.type = 'summary';
        message.body = `${summaryData.summary}`;

        store.dispatch(receiveMessage(message));
      }
    } catch (error) {
      if (needAttachSummaryInChat) {
        message.type = 'summary_error';
        message.body = `Não há mensagens nesta conversa para o dia escolhido.`;

        store.dispatch(receiveMessage(message));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    handleSummarize,
    handleGenerateSummary,
    handlePublishSummary,
    isGeneratingSummary,
    isLoading,
  };
};
